import { useSelector } from 'react-redux';

import * as UserSelectors from 'app/selectors/userSelectors.ts';
import { UserRole } from '@/api-gen/api.schemas';

export interface AccessOptions {
  roles?: UserRole[];
  allowInterpreter?: boolean;
  requireSklls?: boolean;
}

function useHasAccess(options?: AccessOptions): boolean {
  const userRole = useSelector(UserSelectors.RoleSelector);
  const currentUser = useSelector(UserSelectors.CurrentUserSelector);

  const roles = options?.roles;
  const allowInterpreter = !!options?.allowInterpreter;
  const requireSklls = !!options?.requireSklls;

  // Determine if any protection rules are actually active
  const noRolesProtection = !roles || roles.length === 0;
  const noInterpreterProtection = !allowInterpreter;
  const noSkllsProtection = !requireSklls;

  // If no protection rules are defined at all, grant access.
  if (noRolesProtection && noInterpreterProtection && noSkllsProtection) {
    return true;
  }

  // If protection rules ARE defined, but there's no current user, deny access.
  if (!currentUser) {
    return false;
  }

  // At this point, currentUser exists, and some form of protection is active.

  // 1. Primary access check: (Role OR Interpreter)
  // Role check: True if no roles are specified for protection OR if user's role is in the list.
  const roleSatisfied = noRolesProtection || roles.includes(userRole);
  
  // Interpreter check: True if allowInterpreter is set AND user has the flag.
  const interpreterSatisfied = allowInterpreter && currentUser.hasInterpreter === true;
  
  const primaryAccessGranted = roleSatisfied || interpreterSatisfied;

  // 2. Secondary check: requireSklls (ANDed with primary access)
  // Sklls check: True if requireSklls is not set OR if user has the flag.
  const skllsSatisfied = noSkllsProtection || currentUser.hasSklls === true;

  return primaryAccessGranted && skllsSatisfied;
}

export default useHasAccess;
